import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereePaymentHistory } from "types/api-types/payroll/everee-payment-history/everee-payment-history.types";
import type { evereeGrossEarnings } from "types/api-types/payroll/everee-gross-earnings/everee-gross-earnings.types";
import type { evereeExpense } from "types/api-types/payroll/everee-expenses/everee-expenses.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";

export type ReportTab = "payments" | "earnings" | "expenses";

/**
 * EvereeFinancialReportsStore - Manages financial reports for admin interface
 */
class EvereeFinancialReportsStore {
  rootStore: RootStore;

  // Observable Properties
  isLoading = false;
  error: string | null = null;
  activeTab: ReportTab = "payments";
  workerIdFilter = "";
  startDate = "";
  endDate = "";

  // DataGrid state for payments - uses DataGridColumnHeader format (key, title, width)
  paymentHeaders: Array<{ key: string; title: string; width: number }> = [
    { key: "paymentId", title: "Payment ID", width: 120 },
    { key: "workerName", title: "Worker", width: 180 },
    { key: "grossAmount", title: "Gross", width: 100 },
    { key: "netAmount", title: "Net", width: 100 },
    { key: "deductions", title: "Deductions", width: 100 },
    { key: "payDate", title: "Pay Date", width: 120 },
    { key: "status", title: "Status", width: 100 },
    { key: "paymentMethod", title: "Method", width: 100 },
  ];

  // Alias for DataGrid compatibility
  get currentHeaders() {
    return this.paymentHeaders;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ServiceStore accessors
  get evereePaymentHistoryStore(): ServiceStore {
    return this.rootStore.evereePaymentHistoryStore as ServiceStore;
  }

  get evereeGrossEarningsStore(): ServiceStore {
    return this.rootStore.evereeGrossEarningsStore as ServiceStore;
  }

  get evereeExpensesStore(): ServiceStore {
    return this.rootStore.evereeExpensesStore as ServiceStore;
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  // Computed Values
  get payments(): evereePaymentHistory[] {
    return (this.evereePaymentHistoryStore?.entities || []) as unknown as evereePaymentHistory[];
  }

  get grossEarnings(): evereeGrossEarnings[] {
    return (this.evereeGrossEarningsStore?.entities || []) as unknown as evereeGrossEarnings[];
  }

  get expenses(): evereeExpense[] {
    return (this.evereeExpensesStore?.entities || []) as unknown as evereeExpense[];
  }

  get workers(): evereeWorkerData[] {
    return (this.evereeWorkerDataStore?.entities || []) as unknown as evereeWorkerData[];
  }

  get workerMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.workers.forEach((w) => {
      map.set(w.workerId, `${w.firstName} ${w.lastName}`);
    });
    return map;
  }

  get filteredPayments(): evereePaymentHistory[] {
    let result = this.payments;

    if (this.workerIdFilter) {
      result = result.filter((p) => p.workerId === this.workerIdFilter);
    }

    if (this.startDate) {
      result = result.filter((p) => p.payDate >= this.startDate);
    }

    if (this.endDate) {
      result = result.filter((p) => p.payDate <= this.endDate);
    }

    return result;
  }

  // Helper to calculate total deductions from array
  private calculateDeductionsTotal(deductions?: Array<{ amount: { amount: string } }>): number {
    if (!deductions || !Array.isArray(deductions)) return 0;
    return deductions.reduce((sum, d) => sum + parseFloat(d.amount?.amount || "0"), 0);
  }

  get dataGridData(): Record<string, unknown>[] {
    return this.filteredPayments.map((p) => {
      const deductionsTotal = this.calculateDeductionsTotal(p.deductions);
      return {
        id: p.paymentId,
        paymentId: p.paymentId,
        workerId: p.workerId,
        workerName: this.workerMap.get(p.workerId) || p.workerId,
        grossAmount: `$${(p.grossAmount || 0).toFixed(2)}`,
        netAmount: `$${(p.netAmount || 0).toFixed(2)}`,
        deductions: `$${deductionsTotal.toFixed(2)}`,
        payDate: p.payDate ? new Date(p.payDate).toLocaleDateString() : "",
        status: p.status,
        paymentMethod: p.paymentMethod || "N/A",
        hoursWorked: p.hoursWorked || 0,
      };
    });
  }

  get totalCount(): number {
    return this.payments.length;
  }

  get filteredCount(): number {
    return this.filteredPayments.length;
  }

  // Summary calculations
  get paymentSummary() {
    const filtered = this.filteredPayments;
    return {
      totalGross: filtered.reduce((sum, p) => sum + (p.grossAmount || 0), 0),
      totalNet: filtered.reduce((sum, p) => sum + (p.netAmount || 0), 0),
      totalDeductions: filtered.reduce((sum, p) => sum + this.calculateDeductionsTotal(p.deductions), 0),
      totalHours: filtered.reduce((sum, p) => sum + (p.hoursWorked || 0), 0),
      count: filtered.length,
    };
  }

  get expenseSummary() {
    const filtered = this.expenses;
    return {
      totalGrossWages: filtered.reduce((sum, e) => sum + (e.grossWages || 0), 0),
      totalEmployerTaxes: filtered.reduce((sum, e) => sum + (e.employerTaxes || 0), 0),
      totalWorkersComp: filtered.reduce((sum, e) => sum + (e.workersComp || 0), 0),
      totalCost: filtered.reduce((sum, e) => sum + (e.totalCost || 0), 0),
    };
  }

  // Actions
  setActiveTab = (tab: ReportTab) => {
    this.activeTab = tab;
  };

  setWorkerIdFilter = (workerId: string) => {
    this.workerIdFilter = workerId;
  };

  setDateRange = (start: string, end: string) => {
    this.startDate = start;
    this.endDate = end;
  };

  clearFilters = () => {
    this.workerIdFilter = "";
    this.startDate = "";
    this.endDate = "";
  };

  // Async Actions
  loadReports = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;

    try {
      // Build queries with company filter if provided
      const baseQuery: Record<string, unknown> = { $limit: 1000 };
      const workersQuery: Record<string, unknown> = { page: 0, size: 100 };

      if (companyId) {
        baseQuery.companyId = companyId;
        workersQuery.companyId = companyId;
      }

      await Promise.all([
        this.evereePaymentHistoryStore.find({ query: { ...baseQuery } }),
        this.evereeGrossEarningsStore.find({ query: { ...baseQuery } }),
        this.evereeExpensesStore.find({ query: { ...baseQuery } }),
        // Use everee-worker-data for listing workers (everee-workers only supports create/remove)
        this.evereeWorkerDataStore.find({ query: workersQuery }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to load reports";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Export functions
  exportPaymentsToCsv = () => {
    const headers = [
      "Payment ID",
      "Worker ID",
      "Worker Name",
      "Gross Amount",
      "Net Amount",
      "Deductions",
      "Pay Date",
      "Status",
      "Payment Method",
      "Hours Worked",
    ];

    const rows = this.filteredPayments.map((p) => [
      p.paymentId,
      p.workerId,
      this.workerMap.get(p.workerId) || p.workerId,
      (p.grossAmount || 0).toFixed(2),
      (p.netAmount || 0).toFixed(2),
      this.calculateDeductionsTotal(p.deductions).toFixed(2),
      p.payDate,
      p.status,
      p.paymentMethod || "",
      p.hoursWorked || 0,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    this.downloadFile(csvContent, "payment-history.csv", "text/csv");
  };

  exportEarningsToCsv = () => {
    const headers = [
      "Worker ID",
      "Worker Name",
      "YTD Gross Pay",
      "YTD Federal Tax",
      "YTD State Tax",
      "YTD Social Security",
      "YTD Medicare",
    ];

    const rows = this.grossEarnings.map((e) => [
      e.workerId,
      this.workerMap.get(e.workerId) || e.workerId,
      e.yearToDate?.grossPay?.toFixed(2) || "0.00",
      e.yearToDate?.federalTax?.toFixed(2) || "0.00",
      e.yearToDate?.stateTax?.toFixed(2) || "0.00",
      e.yearToDate?.socialSecurity?.toFixed(2) || "0.00",
      e.yearToDate?.medicare?.toFixed(2) || "0.00",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    this.downloadFile(csvContent, "gross-earnings.csv", "text/csv");
  };

  exportExpensesToCsv = () => {
    const headers = [
      "Period Start",
      "Period End",
      "Gross Wages",
      "Employer Taxes",
      "Workers Comp",
      "Total Cost",
    ];

    const rows = this.expenses.map((e) => [
      e.period?.startDate || "",
      e.period?.endDate || "",
      e.grossWages?.toFixed(2) || "0.00",
      e.employerTaxes?.toFixed(2) || "0.00",
      e.workersComp?.toFixed(2) || "0.00",
      e.totalCost?.toFixed(2) || "0.00",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    this.downloadFile(csvContent, "expenses.csv", "text/csv");
  };

  private downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  reset = () => {
    this.isLoading = false;
    this.error = null;
    this.activeTab = "payments";
    this.workerIdFilter = "";
    this.startDate = "";
    this.endDate = "";
  };
}

export default EvereeFinancialReportsStore;
